/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { ComponentChildren, FunctionComponent, VNode, JSX } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import { Divider } from '@adobe-commerce/elsie/components';
import '@adobe-commerce/elsie/components/Header/Header.css';

export interface HeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'size'> {
  title: string;
  size?: 'medium' | 'large';
  divider?: boolean;
  cta?: VNode;
  level?: 1 | 2 | 3 | 4 | 5 | 6;
}

const Heading = ({
  level,
  className,
  children,
}: {
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  className?: string;
  children?: ComponentChildren;
}) => {
  const HeadingTag = level && level >= 1 && level <= 6
    ? `h${level}` as keyof JSX.IntrinsicElements
    : 'span';

  return <HeadingTag className={className}>{children as any}</HeadingTag>;
};

export const Header: FunctionComponent<HeaderProps> = ({
  title = null,
  size = 'medium',
  cta,
  divider = true,
  className,
  level,
  ...props
}) => {
  if (!title) return null;

  return (
    <div
      {...props}
      className={classes(['dropin-header-container', className])}
      data-testid="dropin-header-container"
    >
      <Heading
        className={classes([
          'dropin-header-container__title',
          ['dropin-header-container__title--medium', size === 'medium'],
          ['dropin-header-container__title--large', size === 'large'],
        ])}
        level={level}
      >
        {title}
      </Heading>
      {cta ? (
        <VComponent node={cta} className="dropin-header-container__actions" />
      ) : null}
      {divider ? (
        <Divider
          className={classes([
            'dropin-header-container__divider',
            ['dropin-header-container__divider--medium', size === 'medium'],
            ['dropin-header-container__divider--large', size === 'large'],
          ])}
        />
      ) : null}
    </div>
  );
};
